import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { EntityType } from "constants/index";
import { useDealParamsHooks, useModals } from "hooks";
import { useDealActions, useDocumentsActions } from "store";
import {
  FolderKind,
  IDocumentMetaDetail,
  IFile,
  IFileMetaDetail,
  IFolder,
  IFolderMetaDetail,
  IFormMetaDetail,
  IMetaDetails
} from "store/types";

import { useDealWorkspace } from "../context/DealWorkspace-context";

// ── MIME type constants ───────────────────────────────────────────────────────

export const DND_TYPES = {
  FOLDER: "application/x-folder",
  METADATA_ITEM: "application/x-metadata-item",
  // Set alongside METADATA_ITEM only when the row is a folder — lets drop
  // targets distinguish folder rows from other metadata rows during dragover
  // (actual data cannot be read during dragover due to browser security).
  METADATA_FOLDER: "application/x-metadata-folder"
} as const;

// ── Module-level drag-source tracking ────────────────────────────────────────
// Browser security prevents reading DnD data during dragover, so we track the
// active drag source id at module level (no React state needed).

let draggingFolderId: number | null = null;
let draggingMetadataRowId: number | null = null;

// ── Shared ghost container style ──────────────────────────────────────────────

const GHOST_STYLE =
  "position:fixed;top:-1000px;display:inline-flex;align-items:center;gap:6px;" +
  "padding:5px 10px;background:white;border:1px solid #dbe8f5;border-radius:6px;" +
  "box-shadow:0 4px 12px rgba(0,0,0,0.12);pointer-events:none;max-width:260px;overflow:hidden;";

// ── File-type icon data for drag ghost ───────────────────────────────────────
// Mirrors the fileIconMap in MetadataTable. Each entry has the SVG viewBox and
// path so we can render the correct icon without React/MUI in a plain DOM element.

type IconData = { viewBox: string; path: string };

const SPREADSHEET_ICON: IconData = {
  viewBox: "0 0 12 16",
  path: "M11 15V6H6.5H6V5.5V1H1V15H11ZM7 1.1875V5H10.8125L7 1.1875ZM7.25 0L12 4.75V15V16H11H1H0V15V1V0H1H7.25ZM5.5 8H3V9H5.5V8ZM3 10V11H5.5V10H3ZM6.5 10V11H9V10H6.5ZM3 12V13H5.5V12H3ZM6.5 12V13H9V12H6.5ZM6 14H5.5H3H2V13V12V11.5V11V10V9.5V9V8V7H3H5.5H6H6.5H9H10V8V9V9.5V10V11V11.5V12V13V14H9H6.5H6ZM6.5 9H9V8H6.5V9Z"
};
const TEXT_ICON: IconData = {
  viewBox: "0 0 12 16",
  path: "M11 15V6H6.5H6V5.5V1H1V15H11ZM7 1.1875V5H10.8125L7 1.1875ZM7.25 0L12 4.75V15V16H11H1H0V15V1V0H1H7.25ZM3.5 8H8.5H9V9H8.5H3.5H3V8H3.5ZM3.5 10H8.5H9V11H8.5H3.5H3V10H3.5ZM3.5 12H8.5H9V13H8.5H3.5H3V12H3.5Z"
};

const FILE_TYPE_ICON_DATA: Record<string, IconData> = {
  pdf: {
    viewBox: "0 0 16 16",
    path: "M11 6H6.5H6V5.5V1H1V15H3V16H1H0V15V1V0H1H7.25L12 4.75V9H11V6ZM10.8125 5L7 1.1875V5H10.8125ZM5.5 11H6.5C7.4375 11 8.25 11.8125 8.25 12.75C8.25 13.7188 7.4375 14.5 6.5 14.5H6V15.5V16H5V15.5V14V11.5V11H5.5ZM6.5 13.5C6.90625 13.5 7.25 13.1875 7.25 12.75C7.25 12.3438 6.90625 12 6.5 12H6V13.5H6.5ZM9.5 11H10.5C11.3125 11 12 11.6875 12 12.5V14.5C12 15.3438 11.3125 16 10.5 16H9.5H9V15.5V11.5V11H9.5ZM10.5 15C10.75 15 11 14.7812 11 14.5V12.5C11 12.25 10.75 12 10.5 12H10V15H10.5ZM13 11H13.5H15H15.5V12H15H14V13H15H15.5V14H15H14V15.5V16H13V15.5V13.5V11.5V11Z"
  },
  csv: SPREADSHEET_ICON,
  xlx: SPREADSHEET_ICON,
  xls: SPREADSHEET_ICON,
  xlsx: SPREADSHEET_ICON,
  docx: TEXT_ICON,
  doc: TEXT_ICON,
  document: TEXT_ICON,
  ppt: {
    viewBox: "0 0 12 16",
    path: "M11 15V6H6.5H6V5.5V1H1V15H11ZM7 1.1875V5H10.8125L7 1.1875ZM7.25 0L12 4.75V15V16H11H1H0V15V1V0H1H7.25ZM4 7.5H6.25C7.46875 7.5 8.5 8.53125 8.5 9.75C8.5 11 7.46875 12 6.25 12H4.5V13.5V14H3.5V13.5V11.5V8V7.5H4ZM6.25 11C6.9375 11 7.5 10.4688 7.5 9.75C7.5 9.0625 6.9375 8.5 6.25 8.5H4.5V11H6.25Z"
  },
  pptx: {
    viewBox: "0 0 12 16",
    path: "M11 15V6H6.5H6V5.5V1H1V15H11ZM7 1.1875V5H10.8125L7 1.1875ZM7.25 0L12 4.75V15V16H11H1H0V15V1V0H1H7.25ZM4 7.5H6.25C7.46875 7.5 8.5 8.53125 8.5 9.75C8.5 11 7.46875 12 6.25 12H4.5V13.5V14H3.5V13.5V11.5V8V7.5H4ZM6.25 11C6.9375 11 7.5 10.4688 7.5 9.75C7.5 9.0625 6.9375 8.5 6.25 8.5H4.5V11H6.25Z"
  },
  folder: {
    viewBox: "0 0 20 20",
    path: "M10.5 5H17H18V6V16V17H17H3.00005H2.00005V16V4V3H3.00005H9.00005L10.5 5ZM17 6H10.5H10L9.68755 5.625L8.50005 4H3.00005V16H17V6Z"
  },
  email: {
    viewBox: "0 0 16 12",
    path: "M1 3L8 7.8125L15 3V1H1V3ZM15 4.1875L8 9L1 4.1875V11H15V4.1875ZM0 11V3.5V1V0H1H15H16V1V3.5V11V12H15H1H0V11Z"
  },
  jpg: {
    viewBox: "0 0 12 16",
    path: "M11 15V6H6.5H6V5.5V1H1V15H11ZM7 1.1875V5H10.8125L7 1.1875ZM7.25 0L12 4.75V15V16H11H1H0V15V1V0H1H7.25ZM3.75 8C3.75 8.4375 3.40625 8.75 3 8.75C2.5625 8.75 2.25 8.4375 2.25 8C2.25 7.59375 2.5625 7.25 3 7.25C3.40625 7.25 3.75 7.59375 3.75 8ZM7 8.09375L7.40625 8.75L9.90625 12.75L10 12.875V13V13.5V14H9.5H2.5H2V13.5V13V12.8438L2.09375 12.7188L4.09375 9.96875L4.4375 9.5L4.84375 9.90625L5.40625 10.4688L6.5625 8.75L7 8.09375ZM6.96875 9.9375L5.90625 11.5312L5.5625 12.0625L5.125 11.625L4.53125 11.0312L3.09375 13H8.90625L6.96875 9.9375Z"
  },
  png: {
    viewBox: "0 0 12 16",
    path: "M11 15V6H6.5H6V5.5V1H1V15H11ZM7 1.1875V5H10.8125L7 1.1875ZM7.25 0L12 4.75V15V16H11H1H0V15V1V0H1H7.25ZM3.75 8C3.75 8.4375 3.40625 8.75 3 8.75C2.5625 8.75 2.25 8.4375 2.25 8C2.25 7.59375 2.5625 7.25 3 7.25C3.40625 7.25 3.75 7.59375 3.75 8ZM7 8.09375L7.40625 8.75L9.90625 12.75L10 12.875V13V13.5V14H9.5H2.5H2V13.5V13V12.8438L2.09375 12.7188L4.09375 9.96875L4.4375 9.5L4.84375 9.90625L5.40625 10.4688L6.5625 8.75L7 8.09375ZM6.96875 9.9375L5.90625 11.5312L5.5625 12.0625L5.125 11.625L4.53125 11.0312L3.09375 13H8.90625L6.96875 9.9375Z"
  },
  jpeg: {
    viewBox: "0 0 12 16",
    path: "M11 15V6H6.5H6V5.5V1H1V15H11ZM7 1.1875V5H10.8125L7 1.1875ZM7.25 0L12 4.75V15V16H11H1H0V15V1V0H1H7.25ZM3.75 8C3.75 8.4375 3.40625 8.75 3 8.75C2.5625 8.75 2.25 8.4375 2.25 8C2.25 7.59375 2.5625 7.25 3 7.25C3.40625 7.25 3.75 7.59375 3.75 8ZM7 8.09375L7.40625 8.75L9.90625 12.75L10 12.875V13V13.5V14H9.5H2.5H2V13.5V13V12.8438L2.09375 12.7188L4.09375 9.96875L4.4375 9.5L4.84375 9.90625L5.40625 10.4688L6.5625 8.75L7 8.09375ZM6.96875 9.9375L5.90625 11.5312L5.5625 12.0625L5.125 11.625L4.53125 11.0312L3.09375 13H8.90625L6.96875 9.9375Z"
  },
  note: {
    viewBox: "0 0 14 14",
    path: "M13 1H1V13H8.5V9V8.5H9H13V1ZM12.8125 9.5H9.5V12.8438L12.8125 9.5ZM14 9.75L9.75 14H1H0V13V1V0H1H13H14V1V9.75ZM11.5 3V4.5H2.5V3H11.5ZM2.5 6.25H7.5V7.75H2.5V6.25Z"
  },
  form: {
    viewBox: "0 0 14 14",
    path: "M1 1V13H13V1H1ZM0 0H1H13H14V1V13V14H13H1H0V13V1V0ZM3.25 4C3.25 3.59375 3.5625 3.25 4 3.25C4.40625 3.25 4.75 3.59375 4.75 4C4.75 4.4375 4.40625 4.75 4 4.75C3.5625 4.75 3.25 4.4375 3.25 4ZM6 3.5H6.5H10.5H11V4.5H10.5H6.5H6V3.5ZM6 6.5H6.5H10.5H11V7.5H10.5H6.5H6V6.5ZM6 9.5H6.5H10.5H11V10.5H10.5H6.5H6V9.5ZM4 7.75C3.5625 7.75 3.25 7.4375 3.25 7C3.25 6.59375 3.5625 6.25 4 6.25C4.40625 6.25 4.75 6.59375 4.75 7C4.75 7.4375 4.40625 7.75 4 7.75ZM3.25 10C3.25 9.59375 3.5625 9.25 4 9.25C4.40625 9.25 4.75 9.59375 4.75 10C4.75 10.4375 4.40625 10.75 4 10.75C3.5625 10.75 3.25 10.4375 3.25 10Z"
  },
  // generic fallback
  file: {
    viewBox: "0 0 12 16",
    path: "M11 6H6.5H6V5.5V1H1V15H11V6ZM10.8125 5L7 1.1875V5H10.8125ZM1 0H7.25L12 4.75V15V16H11H1H0V15V1V0H1Z"
  }
};

function createGhostIcon(type: string): SVGSVGElement {
  const iconData = FILE_TYPE_ICON_DATA[type] ?? FILE_TYPE_ICON_DATA.file;
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("viewBox", iconData.viewBox);
  svg.style.flexShrink = "0";
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", iconData.path);
  path.setAttribute("fill", "#71899c");
  svg.appendChild(path);
  return svg;
}

// ── useFolderDragDrop ─────────────────────────────────────────────────────────
// For Folder.tsx — handles both drag source and drop target behaviour.

interface UseFolderDragDropOptions {
  folder: IFolder;
  onDropFiles?: (files: File[]) => void;
}

const DRAG_OVER_DELAY_MS = 150;

export function useFolderDragDrop({ folder, onDropFiles }: UseFolderDragDropOptions) {
  const { refetchFolderMetadata, currentFolder } = useDealWorkspace();
  const [isDragOver, setIsDragOver] = useState(false);
  const dragOverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { updateFormAction, moveFileAction } = useDealActions();
  const { updateDocumentAction } = useDocumentsActions();

  useEffect(() => {
    return () => {
      if (dragOverTimerRef.current !== null) clearTimeout(dragOverTimerRef.current);
    };
  }, []);

  const clearDragOverTimer = () => {
    if (dragOverTimerRef.current !== null) {
      clearTimeout(dragOverTimerRef.current);
      dragOverTimerRef.current = null;
    }
  };

  const onDragStart = (e: React.DragEvent) => {
    if (folder.kind === "dataroom") {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData(DND_TYPES.FOLDER, JSON.stringify(folder));
    e.dataTransfer.effectAllowed = "move";
    draggingFolderId = folder.id;

    const ghost = document.createElement("div");
    ghost.style.cssText = GHOST_STYLE;

    const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    icon.setAttribute("width", "20");
    icon.setAttribute("height", "20");
    icon.setAttribute("viewBox", "0 0 24 24");
    icon.style.flexShrink = "0";
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M10 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z");
    path.setAttribute("fill", "#0A3142");
    icon.appendChild(path);

    const text = document.createElement("span");
    text.style.cssText =
      "font-size:14px;color:#071f35;font-family:inherit;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
    text.textContent = folder.title;

    ghost.appendChild(icon);
    ghost.appendChild(text);
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 12, ghost.offsetHeight / 2 || 16);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };

  const onDragEnd = () => {
    draggingFolderId = null;
    clearDragOverTimer();
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const { types } = e.dataTransfer;
    const isCurrentFolder = folder.id === currentFolder?.id;
    const shouldHighlight =
      ((types.includes(DND_TYPES.METADATA_ITEM) || types.includes(DND_TYPES.METADATA_FOLDER)) &&
        folder.kind !== "dataroom" &&
        !isCurrentFolder) ||
      (types.includes(DND_TYPES.FOLDER) && draggingFolderId !== folder.id) ||
      (types.includes("Files") && !!onDropFiles);

    if (shouldHighlight) {
      if (dragOverTimerRef.current === null && !isDragOver) {
        dragOverTimerRef.current = setTimeout(() => {
          dragOverTimerRef.current = null;
          setIsDragOver(true);
        }, DRAG_OVER_DELAY_MS);
      }
    } else {
      clearDragOverTimer();
    }
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      clearDragOverTimer();
      setIsDragOver(false);
    }
  };

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearDragOverTimer();
    setIsDragOver(false);

    const metadataItemJson = e.dataTransfer.getData(DND_TYPES.METADATA_ITEM);
    if (metadataItemJson && folder.kind !== "dataroom" && folder.id !== currentFolder?.id) {
      const item = JSON.parse(metadataItemJson) as IMetaDetails;
      if (item.entity_type === EntityType.FORM) {
        await updateFormAction({
          dealId: folder.deal_id!,
          formId: item.id,
          section_id: folder.id
        });
        toast.success(`Form ${(item as IFormMetaDetail).title} moved to ${folder.title}`);
        refetchFolderMetadata();
        return;
      }
      if (item.type === EntityType.DOCUMENT) {
        await updateDocumentAction({
          id: item.id,
          section_id: folder.id
        });
        toast.success(`Document ${(item as IDocumentMetaDetail).title} moved to ${folder.title}`);
        refetchFolderMetadata();
        return;
      }
      if (item.entity_type === EntityType.FILE) {
        await moveFileAction({
          file: {
            deal_id: folder.deal_id,
            id: item.id,
            section_id: folder.id
          } as IFile,
          sectionId: folder.id,
          useMethod: "update"
        });
        toast.success(`File ${(item as IFileMetaDetail).name} moved to folder ${folder.title}`);
        refetchFolderMetadata();
        return;
      }
    }

    const folderJson = e.dataTransfer.getData(DND_TYPES.FOLDER);
    if (folderJson && draggingFolderId !== folder.id) {
      // const draggedFolder = JSON.parse(folderJson) as IFolder;
      // TODO: implement move folder to folder
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length && onDropFiles) {
      onDropFiles(files);
    }
  };

  return { isDragOver, onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop };
}

// ── useWorkspaceDrop ──────────────────────────────────────────────────────────
// For Workspace.tsx header — drop target for sidebar folders and table folder rows.

export function useWorkspaceDrop() {
  const [isDragOver, setIsDragOver] = useState(false);
  const { dealId } = useDealParamsHooks();
  const { filesUploadModal } = useModals();

  const onDragOver = (e: React.DragEvent) => {
    const { types } = e.dataTransfer;
    if (types.includes(DND_TYPES.FOLDER) || types.includes(DND_TYPES.METADATA_FOLDER) || types.includes("Files")) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    }
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const folderJson = e.dataTransfer.getData(DND_TYPES.FOLDER);
    if (folderJson) {
      // const draggedFolder = JSON.parse(folderJson) as IFolder;
      // TODO: implement drop sidebar folder onto Workspace root
      return;
    }

    const metadataFolderJson = e.dataTransfer.getData(DND_TYPES.METADATA_FOLDER);
    if (metadataFolderJson) {
      // const draggedFolder = JSON.parse(metadataFolderJson) as IMetaDetails;
      // TODO: implement drop table folder onto Workspace root
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    if (files.length) {
      filesUploadModal({ dealId, files });
    }
  };

  return { isDragOver, onDragOver, onDragLeave, onDrop };
}

// ── useMetadataTableDrag ──────────────────────────────────────────────────────
// For MetadataTable.tsx — drag source for all rows, drop target for folder rows.

export function useMetadataTableDrag(kind: FolderKind, refetchFolderMetadata?: () => void) {
  const [dragOverRowId, setDragOverRowId] = useState<number | null>(null);
  const { updateFormAction, moveFileAction } = useDealActions();
  const { updateDocumentAction } = useDocumentsActions();

  const getRowDragHandlers = (row: IMetaDetails) => {
    const isFolder = row.type === EntityType.FOLDER;

    const onDragStart = (e: React.DragEvent<HTMLTableRowElement>) => {
      if (kind === "dataroom") {
        e.preventDefault();
        return;
      }
      e.dataTransfer.setData(DND_TYPES.METADATA_ITEM, JSON.stringify(row));
      if (isFolder) {
        e.dataTransfer.setData(DND_TYPES.METADATA_FOLDER, JSON.stringify(row));
      }
      e.dataTransfer.effectAllowed = "move";
      draggingMetadataRowId = row.id;

      const ghost = document.createElement("div");
      ghost.style.cssText = GHOST_STYLE;

      // Build ghost from row data — avoids relying on TD index which shifts
      // depending on whether MRT renders the mrt-row-select column.
      const displayName =
        "name" in row ? (row as { name: string }).name : "title" in row ? (row as { title: string }).title : "";
      const text = document.createElement("span");
      text.style.cssText =
        "font-size:14px;color:#071f35;font-family:inherit;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;";
      text.textContent = displayName;

      ghost.appendChild(createGhostIcon(row.type));
      ghost.appendChild(text);

      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 12, ghost.offsetHeight / 2 || 16);
      setTimeout(() => document.body.removeChild(ghost), 0);
    };

    const onDragEnd = () => {
      draggingMetadataRowId = null;
      setDragOverRowId(null);
    };

    const onDragOver = isFolder
      ? (e: React.DragEvent<HTMLTableRowElement>) => {
          const isSelf = draggingMetadataRowId === row.id;
          const { types } = e.dataTransfer;
          if ((types.includes(DND_TYPES.METADATA_ITEM) && !isSelf) || types.includes(DND_TYPES.FOLDER)) {
            e.preventDefault();
            e.stopPropagation();
            setDragOverRowId(row.id);
          }
        }
      : undefined;

    const onDragLeave = isFolder
      ? (e: React.DragEvent<HTMLTableRowElement>) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverRowId(null);
          }
        }
      : undefined;

    const onDrop = isFolder
      ? async (e: React.DragEvent<HTMLTableRowElement>) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOverRowId(null);

          const metadataItemJson = e.dataTransfer.getData(DND_TYPES.METADATA_ITEM);
          if (metadataItemJson) {
            const item = JSON.parse(metadataItemJson) as IMetaDetails;
            const folder = row as IFolderMetaDetail;
            if (item.id !== row.id) {
              if (item.entity_type === EntityType.FORM) {
                await updateFormAction({
                  dealId: folder.deal_id!,
                  formId: item.id,
                  section_id: folder.id
                });
                toast.success(`Form ${(item as IFormMetaDetail).title} moved to ${folder.title}`);
              } else if (item.type === EntityType.DOCUMENT) {
                await updateDocumentAction({
                  id: item.id,
                  section_id: folder.id
                });
                toast.success(`Document ${(item as IDocumentMetaDetail).title} moved to ${folder.title}`);
              } else if (item.entity_type === EntityType.FILE) {
                await moveFileAction({
                  file: {
                    deal_id: folder.deal_id,
                    id: item.id,
                    section_id: folder.id
                  } as IFile,
                  sectionId: folder.id,
                  useMethod: "update"
                });
                toast.success(`File ${(item as IFileMetaDetail).name} moved to folder ${folder.title}`);
              }
              refetchFolderMetadata?.();
            }
            return;
          }

          const folderJson = e.dataTransfer.getData(DND_TYPES.FOLDER);
          if (folderJson) {
            // const draggedFolder = JSON.parse(folderJson) as IFolder;
            // TODO: implement drop sidebar folder onto folder row
          }
        }
      : undefined;

    return {
      isHighlighted: dragOverRowId === row.id,
      draggable: kind !== "dataroom" && !isFolder,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDragLeave,
      onDrop
    };
  };

  return { getRowDragHandlers };
}

// ── useWorkspaceFileDrop ──────────────────────────────────────────────────────
// For WorkspaceMetadata.tsx — drop target for native OS files to upload.

export function useWorkspaceFileDrop() {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const { currentFolder, refetchFolderMetadata } = useDealWorkspace();
  const { dealId } = useDealParamsHooks();
  const { filesUploadModal } = useModals();

  const isNativeFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes("Files");

  useEffect(() => {
    const reset = () => {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    };
    // Capture phase fires before any child's stopPropagation, so we always
    // reset even when a table folder row swallows the drop event.
    document.addEventListener("drop", reset, true);
    // Also reset if the user drags out of the browser window entirely.
    const onDocDragLeave = (e: DragEvent) => {
      if (!e.relatedTarget) reset();
    };
    document.addEventListener("dragleave", onDocDragLeave);
    return () => {
      document.removeEventListener("drop", reset, true);
      document.removeEventListener("dragleave", onDocDragLeave);
    };
  }, []);

  const handleDragEnter = (e: React.DragEvent) => {
    if (!isNativeFileDrag(e)) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragOver(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isNativeFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!isNativeFileDrag(e)) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    filesUploadModal({
      dealId,
      folderId: currentFolder?.id || undefined,
      files,
      onSuccessCallback: () => refetchFolderMetadata()
    });
  };

  return { isDragOver, handleDragEnter, handleDragOver, handleDragLeave, handleDrop };
}
